/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ForwardedRef, forwardRef, MouseEvent as ReactMouseEvent} from 'react';
import ClockDialOptions from './ClockDialOptions';
import ClockDialPointer from './ClockDialPointer';
import {ClockDialView, getSelectedClockLabel, getSelectedHandlePosition} from './ClockDialUtils';

export interface ClockDialFaceProps {
  classes: Record<string, string>;
  onFaceMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onOptionClick: (value: number) => (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onOptionMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  options: number[];
  selectedHour: number;
  selectedMinute: number;
  view: ClockDialView | null;
}

/**
 * Renders the complete interactive clock face, combining the pointer,
 * the selectable option labels, and the draggable selected-value handle.
 *
 * ```typescript
 *
 * import {ClockDialFace} from '@ion/dls/src/components/date/ClockDialFace';
 *
 * <ClockDialFace ref={faceRef}
 *   classes={classes}
 *   view={ClockDialView.Minutes}
 *   options={options}
 *   selectedHour={9}
 *   selectedMinute={30}
 *   onFaceMouseDown={onFaceMouseDown}
 *   onOptionClick={onOptionClick}
 *   onOptionMouseDown={onOptionMouseDown} />
 *
 * ```
 */
export const ClockDialFace = forwardRef(({
  classes,
  onFaceMouseDown,
  onOptionClick,
  onOptionMouseDown,
  options,
  selectedHour,
  selectedMinute,
  view
}: ClockDialFaceProps, ref: ForwardedRef<HTMLDivElement>) => (
  <div className={classes.face}
    ref={ref}
    data-testid='digital-time-picker-clock-face'
    onMouseDown={onFaceMouseDown}>
    <div className={classes.pin} />
    <ClockDialPointer classes={classes}
      selectedHour={selectedHour}
      selectedMinute={selectedMinute}
      view={view} />
    <ClockDialOptions classes={classes}
      onOptionClick={onOptionClick}
      onOptionMouseDown={onOptionMouseDown}
      options={options}
      selectedHour={selectedHour}
      selectedMinute={selectedMinute}
      view={view} />
    <button type='button'
      className={classes.dragHandle}
      data-testid='digital-time-picker-clock-drag-handle'
      style={getSelectedHandlePosition(view, selectedHour, selectedMinute)}
      onMouseDown={onFaceMouseDown}
      aria-label='Drag clock hand'>
      {getSelectedClockLabel(view, selectedHour, selectedMinute)}
    </button>
  </div>
));

export default ClockDialFace;
